import { useEffect, useMemo, useState, Suspense } from "react";
import {
  useParams,
  useRouteContext,
  useNavigate,
} from "@tanstack/react-router";
import {
  type GridRowClickEvent,
  type GridColumnProps,
} from "@progress/kendo-react-grid";
import { useLeaData } from "@/queries.ts";
import { CHLD, CHLD_Key } from "@/models/chld.tsx";
import LayoutContentArea from "@/components/layout/LayoutContentArea";
import Card from "@/components/shared/Card";
import KendoTableClientState from "@/components/shared/tables/KendoTableClientState";
import {
  ChildSearchForm,
  ChildSearchFormState,
} from "@/components/records/child/ChildSearchForm";
import { formatDateOnly, getCurrentProgramYear } from "@/utils/dates";
import { AddChildWizard } from "@/components/records/child/add-child-wizard/AddChildWizard";
import { useAddChild } from "@/components/layout/AddChildContext";
import { CSVDownloadButton } from "@/components/shared/CSVDownloadButton";
import {
  ChildSearchTabs,
  type ChildSearchTab,
} from "@/components/records/child/ChildSearchTabs";
import { StateSearchView } from "@/components/records/child/StateSearchView";
import { CSPPIDMatchToolView } from "@/components/records/child/CSPPIDMatchToolView";

type PreschoolSummary = {
  PreschoolCDSCode: string;
  PreschoolName: string;
};

type ChildRow = CHLD & {
  PreschoolName?: string;
  ChildName: string;
};

const childCsvHeaders = [
  { label: "School", key: "PreschoolName" },
  { label: "Child Name", key: "ChildName" },
  { label: "CSPPID", key: "CSPPID" },
  { label: "Local Child ID (CICN)", key: "LocalChildId" },
  { label: "Date of Birth", key: "ChildDateOfBirth" },
];

function StateSearchSkeleton() {
  return (
    <Card className="card-custom shadow-none">
      <div className="d-flex align-items-center justify-content-center py-5 gap-3">
        <div
          className="spinner-border spinner-border-sm"
          role="status"
          aria-hidden="true"
          style={{ color: "#0d2d4a" }}
        />
        <span style={{ color: "#0d2d4a" }}>Loading state search...</span>
      </div>
    </Card>
  );
}

const validTabs: ChildSearchTab[] = ["lea", "state", "match"];

function getTabFromHash(): ChildSearchTab {
  const hash = window.location.hash.replace("#", "");
  return validTabs.includes(hash as ChildSearchTab)
    ? (hash as ChildSearchTab)
    : "lea";
}

export default function ChildTablePage() {
  const leaContext = useRouteContext({ from: "/$leaid" });

  const leaId = useParams({ from: "/$leaid" })?.leaid;
  const { data: leaData, error, isLoading } = useLeaData(leaId);

  const navigate = useNavigate({ from: "/$leaid" });

  const preschools: PreschoolSummary[] = leaContext?.currentOrgPreschools ?? [];

  const childRecords = useMemo<CHLD[]>(() => {
    if (!leaData) return [];
    return Object.values(leaData.entities.children).map((x) => x.records[0]);
  }, [leaData]);

  const tableData: ChildRow[] = useMemo(() => {
    return childRecords.map((record) => ({
      ...record,
      PreschoolName: preschools.find(
        (x) => x.PreschoolCDSCode === record.PreschoolCode
      )?.PreschoolName,
      PreschoolCode: record.PreschoolCode,
      ChildName: [
        record.ChildFirstName,
        record.ChildMiddleName,
        record.ChildLastName,
      ]
        .filter(Boolean)
        .join(" "),
      CSPPID: record.CSPPID,
      LocalChildId: record.LocalChildId,
      ChildDateOfBirth: record.ChildDateOfBirth
        ? formatDateOnly(record.ChildDateOfBirth)
        : "",
      ProgramYear: record.ProgramYear,
    }));
  }, [childRecords, preschools]);

  const columns: GridColumnProps[] = [
    { id: "PreschoolName", field: "PreschoolName", title: "School" },
    { id: "ChildName", field: "ChildName", title: "Child Name" },
    { id: "CSPPID", field: "CSPPID", title: "CSPPID" },
    {
      id: "LocalChildId",
      field: "LocalChildId",
      title: "Local Child ID (CICN)",
    },
    {
      id: "ChildDateOfBirth",
      field: "ChildDateOfBirth",
      title: "Date of Birth",
    },
  ];

  const programYearOptions = useMemo(
    () =>
      Array.from(
        new Set(childRecords.map((c) => c.ProgramYear).filter(Boolean))
      ),
    [childRecords]
  );

  const isInitialLoadDone = !isLoading;

  const [activeTab, setActiveTabState] =
    useState<ChildSearchTab>(getTabFromHash);

  const setActiveTab = (tab: ChildSearchTab) => {
    setActiveTabState(tab);
    window.history.pushState(null, "", `#${tab}`);
  };

  useEffect(() => {
    const onPopState = () => setActiveTabState(getTabFromHash());
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const [appliedFilters, setAppliedFilters] = useState<ChildSearchFormState>(
    () => ({
      programYear: getCurrentProgramYear(),
      schools: [],
      dateOfBirth: "",
      firstName: "",
      middleName: "",
      lastName: "",
    })
  );

  const [showWizard, setShowWizard] = useState(false);
  const { registerOpenHandler, registerTabHandler } = useAddChild();

  useEffect(() => {
    if (localStorage.getItem("openAddChildWizard") === "true") {
      localStorage.removeItem("openAddChildWizard");
      setShowWizard(true);
    }
    registerOpenHandler(() => setShowWizard(true));
    registerTabHandler((tab) => setActiveTab(tab));
  }, [registerOpenHandler, registerTabHandler]);

  const handleSearch = (filters: ChildSearchFormState) => {
    if (!isInitialLoadDone) return;
    setAppliedFilters(filters);
  };

  const handleClearFilters = () => {
    navigate({
      to: "/$leaid/records/child",
      params: { leaid: leaId },
    });
  };

  const filteredData: ChildRow[] = useMemo(() => {
    if (!isInitialLoadDone) return tableData;

    const selectedSchoolCodes = appliedFilters.schools.map(
      (s) => s.PreschoolCDSCode
    );

    const year = appliedFilters.programYear.trim();
    const first = appliedFilters.firstName.trim().toLowerCase();
    const middle = appliedFilters.middleName.trim().toLowerCase();
    const last = appliedFilters.lastName.trim().toLowerCase();

    return tableData.filter((row) => {
      if (year && row.ProgramYear !== year) return false;

      if (
        selectedSchoolCodes.length > 0 &&
        !selectedSchoolCodes.includes(row.PreschoolCode ?? "")
      ) {
        return false;
      }

      if (appliedFilters.dateOfBirth) {
        const [year, month, day] = appliedFilters.dateOfBirth.split("-");
        const filterDob = `${month}/${day}/${year}`;
        if (row.ChildDateOfBirth !== filterDob) return false;
      }

      if (first) {
        const rowFirst = (row.ChildFirstName ?? "").toLowerCase();
        if (!rowFirst.includes(first)) return false;
      }

      if (middle) {
        const rowMiddle = (row.ChildMiddleName ?? "").toLowerCase();
        if (!rowMiddle.includes(middle)) return false;
      }

      if (last) {
        const rowLast = (row.ChildLastName ?? "").toLowerCase();
        if (!rowLast.includes(last)) return false;
      }

      return true;
    });
  }, [tableData, appliedFilters, isInitialLoadDone]);

  return (
    <LayoutContentArea
      headingText="Child Records"
      headerActions={
        <HeaderActions
          onAddButtonClick={() => setShowWizard(true)}
          data={filteredData}
        />
      }
    >
      <AddChildWizard
        show={showWizard}
        onClose={() => setShowWizard(false)}
        preschools={preschools}
      />

      <ChildSearchTabs activeTab={activeTab} onTabChange={setActiveTab} />

      {activeTab === "lea" && (
        <>
          <Card className="card-custom mb-5 shadow-none">
            <ChildSearchForm
              programYearOptions={programYearOptions}
              initialProgramYear={getCurrentProgramYear()}
              preschools={preschools}
              disabled={!isInitialLoadDone}
              onSearch={handleSearch}
              onClear={handleClearFilters}
            />
          </Card>

          <Card className="table-style mb-3">
            <KendoTableClientState
              key={[
                appliedFilters.programYear,
                appliedFilters.firstName,
                appliedFilters.middleName,
                appliedFilters.lastName,
                appliedFilters.dateOfBirth,
                appliedFilters.schools.map((s) => s.PreschoolCDSCode).join(","),
              ].join("-")}
              data={filteredData}
              columns={columns}
              error={error}
              filterable={false}
              onRowClick={(event: GridRowClickEvent) => {
                const child: CHLD = event.dataItem as CHLD;
                const entityId = CHLD_Key(child);
                navigate({
                  to: "/$leaid/records/child/$entityid",
                  params: { leaid: leaId, entityid: entityId },
                }).catch((error) => {
                  console.error("Navigation error:", error);
                });
              }}
            />
          </Card>
        </>
      )}

      {activeTab === "state" && (
        <Suspense fallback={<StateSearchSkeleton />}>
          <StateSearchView key="state-search" />
        </Suspense>
      )}

      {activeTab === "match" && <CSPPIDMatchToolView key="match-tool" />}
    </LayoutContentArea>
  );
}

function HeaderActions({
  onAddButtonClick,
  data,
}: {
  onAddButtonClick: () => void;
  data: ChildRow[];
}) {
  const leaContext = useRouteContext({ from: "/$leaid" });

  return (
    <div className="d-flex align-items-center">
      <CSVDownloadButton
        data={data}
        headers={childCsvHeaders}
        filename="child-records.csv"
      />
      {leaContext.hasRole("CHLDEdit") && (
        <button
          type="button"
          className="btn btn-primary d-flex align-items-center gap-2"
          onClick={onAddButtonClick}
        >
          <i className="bi bi-person-plus"></i>
          Add Child
        </button>
      )}
    </div>
  );
}
